import React, { useMemo } from 'react';
import moment from 'moment-timezone';

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const STATUS_STYLES = {
  present: 'bg-green-100 text-green-800 border-green-200',
  checkoutMissing: 'bg-orange-100 text-orange-800 border-orange-200',
  leave: 'bg-purple-100 text-purple-800 border-purple-200',
  wfh: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  absent: 'bg-red-100 text-red-800 border-red-200',
  empty: 'bg-gray-100 text-gray-500 border-gray-200',
};

const formatHours = (value) => {
  const numericValue = Number(value || 0);
  return Number.isInteger(numericValue) ? numericValue.toFixed(0) : numericValue.toFixed(1);
};

const getApprovedLeaveForDate = (member, dateKey) => {
  return (member?.leaveRequests || []).find((leave) => {
    if ((leave.status || '').toLowerCase() !== 'approved') return false;
    if (leave.leaveType === 'Short Leave') return false;

    const start = moment(leave.startDate).format('YYYY-MM-DD');
    const end = moment(leave.endDate).format('YYYY-MM-DD');
    return dateKey >= start && dateKey <= end;
  });
};

const buildDailyAttendanceMap = (dailyHours = []) => {
  return dailyHours.reduce((acc, day) => {
    acc[moment(day.date).format('YYYY-MM-DD')] = day;
    return acc;
  }, {});
};

const buildCalendarWeeks = (startDate, endDate) => {
  const start = moment(startDate).startOf('day');
  const end = moment(endDate).startOf('day');
  const calendarStart = start.clone().startOf('isoWeek');
  const calendarEnd = end.clone().endOf('isoWeek');
  const weeks = [];
  const cursor = calendarStart.clone();

  while (cursor.isSameOrBefore(calendarEnd, 'day')) {
    const week = [];
    for (let index = 0; index < 7; index += 1) {
      week.push(cursor.clone());
      cursor.add(1, 'day');
    }
    weeks.push(week);
  }

  return weeks;
};

export const getMemberCalendarDayStatus = ({ member, date, dailyMap, startDate, endDate, today }) => {
  const dateKey = date.format('YYYY-MM-DD');
  const start = moment(startDate).startOf('day');
  const end = moment(endDate).startOf('day');
  const todayMoment = moment(today).startOf('day');
  const isInRange = date.isSameOrAfter(start, 'day') && date.isSameOrBefore(end, 'day');
  const attendance = dailyMap[dateKey];
  const leave = getApprovedLeaveForDate(member, dateKey);
  const isFuture = date.isAfter(todayMoment, 'day');
  const isWeekend = date.day() === 0 || date.day() === 6;

  if (!isInRange) {
    return {
      key: 'empty',
      label: 'No activity',
      subLabel: '',
      className: STATUS_STYLES.empty,
      isInRange,
    };
  }

  if (leave?.leaveType === 'Work from Home') {
    return {
      key: 'wfh',
      label: 'WFH',
      subLabel: leave.leaveType,
      className: STATUS_STYLES.wfh,
      isInRange,
    };
  }

  if (leave) {
    return {
      key: 'leave',
      label: leave.leaveType || 'Leave',
      subLabel: 'Approved leave',
      className: STATUS_STYLES.leave,
      isInRange,
    };
  }

  if (attendance) {
    const totalHours = Number(attendance.hoursWorked || 0) + Number(attendance.extraHours || 0);
    const hasCheckIn = Boolean(attendance.firstCheckIn);
    const hasCheckOut = Boolean(attendance.lastCheckOut);

    if (hasCheckIn && !hasCheckOut && totalHours <= 0) {
      return {
        key: 'checkoutMissing',
        label: 'Checkout Missing',
        subLabel: moment(attendance.firstCheckIn).tz('Asia/Kolkata').format('HH:mm'),
        className: STATUS_STYLES.checkoutMissing,
        isInRange,
      };
    }

    if (totalHours > 0 || hasCheckOut) {
      return {
        key: 'present',
        label: `${formatHours(totalHours)}h`,
        subLabel: hasCheckIn && hasCheckOut
          ? `${moment(attendance.firstCheckIn).tz('Asia/Kolkata').format('HH:mm')} - ${moment(attendance.lastCheckOut).tz('Asia/Kolkata').format('HH:mm')}`
          : 'Present',
        className: STATUS_STYLES.present,
        isInRange,
      };
    }
  }

  if (!isFuture && !isWeekend) {
    return {
      key: 'absent',
      label: 'Absent',
      subLabel: 'No attendance found',
      className: STATUS_STYLES.absent,
      isInRange,
    };
  }

  return {
    key: 'empty',
    label: 'No activity',
    subLabel: '',
    className: STATUS_STYLES.empty,
    isInRange,
  };
};

export default function MemberAttendanceCalendar({
  member,
  startDate,
  endDate,
  today = new Date(),
}) {
  const dailyMap = useMemo(() => buildDailyAttendanceMap(member?.dailyHours || []), [member]);
  const weeks = useMemo(() => buildCalendarWeeks(startDate, endDate), [startDate, endDate]);
  const rangeLabel = `${moment(startDate).format('MMM DD, YYYY')} - ${moment(endDate).format('MMM DD, YYYY')}`;

  return (
    <div>
      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h5 className="text-sm font-semibold text-blue-700">Attendance Calendar</h5>
          <p className="text-xs text-gray-500">{rangeLabel}</p>
        </div>
        <div className="flex flex-wrap gap-2 text-[11px] font-semibold text-gray-600">
          <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-green-500" />Present</span>
          <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-orange-500" />Checkout Missing</span>
          <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-purple-500" />Leave</span>
          <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-red-500" />Absent</span>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
        <div className="grid min-w-[720px] grid-cols-7 border-b border-gray-200 bg-gray-100">
          {DAY_NAMES.map((day) => (
            <div key={day} className="border-r border-gray-200 px-2 py-2 text-center text-xs font-bold uppercase text-gray-700 last:border-r-0">
              {day}
            </div>
          ))}
        </div>
        <div className="min-w-[720px]">
          {weeks.map((week, weekIndex) => (
            <div key={weekIndex} className="grid grid-cols-7 border-b border-gray-200 last:border-b-0">
              {week.map((date) => {
                const dateKey = date.format('YYYY-MM-DD');
                const isToday = date.isSame(moment(today), 'day');
                const status = getMemberCalendarDayStatus({
                  member,
                  date,
                  dailyMap,
                  startDate,
                  endDate,
                  today,
                });

                return (
                  <div
                    key={dateKey}
                    className={`min-h-[96px] border-r border-gray-200 p-2 last:border-r-0 ${
                      status.isInRange ? 'bg-white' : 'bg-gray-50 opacity-50'
                    } ${isToday ? 'ring-2 ring-blue-400 ring-inset' : ''}`}
                  >
                    <div className="mb-2 flex items-center justify-between">
                      <span className={`text-sm font-bold ${isToday ? 'text-blue-700' : 'text-gray-800'}`}>
                        {date.format('D')}
                      </span>
                      <span className="text-[10px] font-medium text-gray-400">{date.format('MMM')}</span>
                    </div>
                    <div className={`inline-flex max-w-full rounded-full border px-2 py-1 text-[11px] font-semibold ${status.className}`}>
                      <span className="truncate">{status.label}</span>
                    </div>
                    {status.subLabel && (
                      <div className="mt-1 truncate text-[11px] font-medium text-gray-500" title={status.subLabel}>
                        {status.subLabel}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
